import {
  CONTAINER_HEIGHT_UNIT_OPTIONS,
  DEFAULT_CONTAINER_HEIGHT,
  type ContainerHeight,
  type ContainerHeightUnit,
} from "@/lib/cms-puck/layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import { cn } from "@/lib/utils";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

type ContainerHeightControlsProps = {
  value?: ContainerHeight;
  onChange: (value: ContainerHeight) => void;
  inputClassName?: string;
  selectTriggerClassName?: string;
};

/** Input + select unité (sans libellé), pour réutilisation dans ControlRow. */
export function ContainerHeightControls({
  value,
  onChange,
  inputClassName,
  selectTriggerClassName,
}: ContainerHeightControlsProps) {
  const cmsNew = useCmsNew();
  const current = value ?? DEFAULT_CONTAINER_HEIGHT;

  return (
    <>
      <Input
        type="number"
        min={0}
        step="any"
        placeholder={cmsNew("cms.new.auto.placeholder.auto", "Auto")}
        className={cn("h-9 w-24 text-center tabular-nums", inputClassName)}
        value={current.value ?? ""}
        onChange={(e) => {
          const raw = e.target.value.trim();
          onChange({
            ...current,
            value:
              raw === ""
                ? null
                : Number.isFinite(Number(raw))
                  ? Number(raw)
                  : current.value,
          });
        }}
      />
      <Select
        value={current.unit}
        onValueChange={(unit) =>
          onChange({ ...current, unit: unit as ContainerHeightUnit })
        }
      >
        <SelectTrigger className={cn("h-9 w-20 bg-white", selectTriggerClassName)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONTAINER_HEIGHT_UNIT_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
}

/** Hauteur explicite du conteneur — variante autonome (legacy / standalone). */
export function ContainerHeightField({
  value,
  onChange,
}: {
  value?: ContainerHeight;
  onChange: (value: ContainerHeight) => void;
}) {
  const cmsNew = useCmsNew();
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600">{cmsNew("cms.new.auto.text.hauteur", "Hauteur")}</Label>
      <div className="flex gap-2">
        <ContainerHeightControls
          value={value}
          onChange={onChange}
          inputClassName="min-w-0 flex-1"
          selectTriggerClassName="w-[88px] shrink-0"
        />
      </div>
      <p className="text-[11px] leading-snug text-slate-500">
        Laissez vide pour une hauteur automatique.
      </p>
    </div>
  );
}
